import React, { useEffect, useState } from 'react';
import CanvasJSReact from '@canvasjs/react-charts';
import {
  Box,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { Users } from 'lucide-react';
import { dashboardAPI } from '../services/api.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const CHART_HEIGHT = 400;

const UsersGraph = () => {
  const { theme } = useTheme();
  const mantine = useMantineTheme();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await dashboardAPI.getUsersGraph();
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Transform object into an array format suitable for CanvasJS
  const chartData = stats
    ? Object.entries(stats).map(([name, value]) => ({
        label: name,
        y: value,
      }))
    : [];

  const isDark = theme === 'dark';

  // The chart is drawn on a canvas, so it cannot read CSS variables: its
  // colours are picked from the Mantine palette for the scheme on screen.
  const { colors, white, black, primaryColor, fontFamily } = mantine;
  const textColor = isDark ? colors.dark[1] : colors.gray[7];
  const gridColor = isDark ? colors.dark[5] : colors.gray[2];
  const axisColor = isDark ? colors.dark[4] : colors.gray[4];
  const surfaceColor = isDark ? colors.dark[6] : white;

  const options = {
    animationEnabled: true,
    height: CHART_HEIGHT,
    theme: isDark ? 'dark2' : 'light2',
    backgroundColor: 'transparent',
    title: {
      fontSize: 0,
    },
    axisX: {
      labelFontFamily: fontFamily,
      labelFontSize: 12,
      labelFontColor: textColor,
      lineColor: axisColor,
      tickColor: axisColor,
    },
    axisY: {
      gridColor,
      lineColor: axisColor,
      tickColor: axisColor,
      labelFontFamily: fontFamily,
      labelFontSize: 12,
      labelFontColor: textColor,
    },
    toolTip: {
      backgroundColor: surfaceColor,
      fontColor: isDark ? white : black,
      borderThickness: 0,
      cornerRadius: 8,
      fontFamily,
    },
    data: [
      {
        type: 'column',
        color: colors[primaryColor][isDark ? 5 : 7],
        indexLabelFontFamily: fontFamily,
        indexLabelFontSize: 12,
        indexLabelFontColor: textColor,
        dataPoints: chartData,
      },
    ],
  };

  return (
    <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
      <Group gap="sm" wrap="nowrap" mb="lg">
        <ThemeIcon size="lg" variant="light" color="grape">
          <Users size={18} />
        </ThemeIcon>
        <Stack gap={0}>
          <Title order={4}>Users Per Company</Title>
          <Text size="sm" c="dimmed">
            Distribution of users across companies
          </Text>
        </Stack>
      </Group>

      <Box mih={CHART_HEIGHT}>
        {loading ? (
          <Stack align="center" justify="center" gap="xs" h={CHART_HEIGHT}>
            <Loader />
            <Text size="sm" c="dimmed">
              Loading data...
            </Text>
          </Stack>
        ) : (
          <CanvasJSChart options={options} />
        )}
      </Box>
    </Paper>
  );
};

export default UsersGraph;
